import { z } from 'zod';

import { DEFAULT_CURRENCY } from '../Config';
import { ExclusiveTreatmentCalculator } from '../models/Treatment/ExclusiveTreatmentCalculator';
import { InclusiveTreatmentCalculator } from '../models/Treatment/InclusiveTreatmentCalculator';
import { NotApplicableTreatmentCalculator } from '../models/Treatment/NotApplicableTreatmentCalculator';
import { TreatmentCalculator } from '../models/Treatment/TreatmentCalculator';

export type TaxRate = {
  name: string;
  id: string;
  description: string;
  rate: number;
};

export type Currency = {
  id: string;
  name: string;
  default: boolean;
};

export interface PublishData {
  id: string;
  name: string;
}

export const dropdownOptionSchema = z.object({
  label: z.string(),
  value: z.union([z.string(), z.number()]),
  publishData: z.object({
    id: z.string(),
    name: z.string(),
  }),
});

export type SelectCommon = {
  value: string;
  label: string;
};

export type TaxCalculation = 'Inclusive of Tax' | 'Exclusive of Tax' | 'Out of Scope';

export interface DropdownOption {
  label?: string;
  value?: string | number;
  publishData?: PublishData;
}

export type CalculationStrategy =
  | typeof TreatmentCalculator
  | typeof InclusiveTreatmentCalculator
  | typeof ExclusiveTreatmentCalculator
  | typeof NotApplicableTreatmentCalculator;

export type LineEntry = {
  item: {
    id: string;
    name: string;
  };
  account: { id: string; name: string };
  description: string;
  taxCode: {
    id: string;
    name: string;
    taxRate: number;
  } & {
    rates: TaxRate[];
  };
  qty: string | number;
  unitPrice: string | number;
  totalAmount: string | number;
  class: {
    id: string;
    name: string;
  };
};

export interface RemoteOptionsResponse {
  accounts: {
    [key: string]: {
      name: string;
      type: string;
    };
  };
  bankAccounts: {
    [key: string]: {
      name: string;
      type: string;
    };
  };
  classes: {
    [key: string]: string;
  };
  currencies: Currency[];
  defaultCurrency: Currency;
  defaultMemo: string;
  items: {
    [key: string]: {
      name: string;
      type: string;
      price: number;
      taxIncluded: boolean;
    };
  };
  employees: {
    [key: string]: {
      name: string;
      type: string;
    };
  };
  locations: {
    [key: string]: string;
  };
  paymentAccounts: {
    [key: string]: {
      name: string;
      type: string;
    };
  };
  paymentMethods: {
    [key: string]: string;
  };
  taxCalculation: {
    [key: string]: string;
  };
  taxCodes: {
    [key: string]: {
      name: string;
      description: string;
      rates: TaxRate[];
    };
  };
  terms: {
    [key: string]: string;
  };
  vendors: {
    [key: string]: {
      name: string;
      currency: {
        value: string;
        name: string;
      };
    };
  };
}

export type RemoteOptions = {
  lineItems?: {
    items?: {
      label: string;
      value: string;
      publishData: PublishData;
      type: string;
    }[];
    accounts?: DropdownOption[];
    taxCodes?: {
      label: string;
      value: string;
      publishData: PublishData & TaxRate[];
      taxRate: number;
    }[];
    classes?: DropdownOption[];
    taxCalculation?: {
      label: string;
      value: string;
      publishData: string;
      strategy: CalculationStrategy;
    }[];
  };
  defaultMemo?: string;
  defaultCurrency?: Currency;
  currencies?: DropdownOption[];
  payees?: {
    type: string;
    label: string;
    value: string;
    publishData: PublishData;
    currency: {
      value: string;
      name: string;
    };
  }[];
  bankAccounts?: DropdownOption[];
  terms?: DropdownOption[];
  locations?: DropdownOption[];
  paymentAccounts?: DropdownOption[];
  paymentMethods?: DropdownOption[];
};

export type UnpaidBillCommonPayload = CommonPayload & {
  vendor: PublishData;
  dueDate: string;
  salesTerm: PublishData;
};

export type PaidBillCommonPayload = CommonPayload & {
  payee: PublishData;
  paymentAccount: PublishData;
  paymentMethod?: PublishData;
};

export type CheckPaymentCommonPayload = CommonPayload & {
  bankAccount: PublishData;
  payee: PublishData;
};

export type CommonPayload = {
  currency: Currency | typeof DEFAULT_CURRENCY;
  memo?: string;
  referenceNumber?: string;
  date: string;
  taxCalculation: TaxCalculation;
  totalAmount: number;
  transactionDate: string;
  location?: PublishData;
};
